/*
 *  -----------------------------------------------------------------------
 *    <copyright file="manage-actions.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { getDashboardPanels } from '@components/central-monitoring/manage-observation/manage-observation.helper';
import ManageObservation from '../../components/central-monitoring/manage-observation/manage-observation';
import { ICMObservationAndActionExtended } from './manage-observations.interface';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { Permissions } from 'contexts/user-context';
import { useTranslation, useBoolean, useAuthorization, useLocalState } from 'hooks';
import { GET_CM_OBSERVATIONS_AND_ACTIONS, GET_DATASET_REVIEWS, GET_DATASETS } from 'queries/central-monitoring';
import { GET_SITES, GET_SITES_DELETE } from 'queries/sites';
import { GET_RESTRICTED_STUDY } from 'queries/studies';
import Constants from 'utils/constants';
import NotificationService from 'utils/notification';
import ActionsGridV2 from 'views/components/central-monitoring/actions-grid-v2';
import AddAction from 'views/components/central-monitoring/add-action';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import { SelectWithSearchDataArrayInterface } from 'views/components/core/select-with-categories/select-with-categories.interface';
import Error from 'views/components/error/error';
import { getDatasetDates, getPanelName } from '@utils/cm-observations.service';
import { AllRoutes } from '@utils/router/all-routes';
import {
    dataSets,
    dataSetsVariables,
    dataSetReviews,
    dataSetReviewsVariables,
} from '@queries/generated/central-monitoring.generated';
import {
    SiteFields,
    StudyFields_Study_members_StudyMember,
    PanelForObservationFields,
    DataSetFields,
    StudyFields,
    CentralMonitoringActionFields,
} from '@queries/generated/fragments.generated';
import { riskObservations_OpraQueries_riskObservations_RiskObservation } from '@queries/generated/legacy-risk-assessment.generated';

const ManageCMActions = ({
    study,
    handleRestoreAct,
    handleDeleteAct,
}: {
    study: StudyFields;
    handleRestoreAct?: Function;
    handleDeleteAct?: Function;
}) => {
    const { t } = useTranslation();
    const { state } = useLocalState();
    const navigate = useNavigate();

    const [loadData, { loading, error, data }] = useLazyQuery(GET_CM_OBSERVATIONS_AND_ACTIONS);
    const [loadStudyData, { loading: loadingStudy, data: studyData }] = useLazyQuery(GET_RESTRICTED_STUDY);
    const [loadDataSets, { data: dataSetsData }] = useLazyQuery<dataSets, dataSetsVariables>(GET_DATASETS);
    const [loadDataSetsReviews, { data: dataSetsReviewsData }] = useLazyQuery<dataSetReviews, dataSetReviewsVariables>(
        GET_DATASET_REVIEWS,
    );
    const { data: dataSites } = useQuery(GET_SITES, {
        variables: {
            studyId: study?.id ?? '',
        },
    });
    const { data: siteDeletedData } = useQuery(GET_SITES_DELETE, {
        variables: {
            studyId: study?.id,
        },
    });

    const user = useAuthorization();
    const canView = user?.canRead({
        module: Constants.Modules.CM,
        element: Constants.DataElements.CentralMonitoring,
        permission: Permissions.Read,
    });
    const canEdit = user?.canWrite({
        module: Constants.Modules.CM,
        element: Constants.DataElements.CentralMonitoring,
        permission: Permissions.ReadWrite,
    });

    const [actions, setActions] = useState([]);
    const [currentAction, setCurrentAction] = useState(null);
    const [currentObservation, setCurrentObservation] = useState(null);
    const [ownerOptions, setOwnerOptions] = useState([] as SelectWithSearchDataArrayInterface[]);
    const [datasetDates, setDatasetDates] = useState<DataSetFields[]>(null);
    const [panels, setPanels] = useState<PanelForObservationFields[]>([]);
    const addActionOpen = useBoolean(false);
    const manageObservationOpen = useBoolean(false);

    useEffect(() => {
        const datasetDatesResult = getDatasetDates(dataSetsData, dataSetsReviewsData, user.systemAdminLevel);

        setDatasetDates(datasetDatesResult);
    }, [dataSetsData, dataSetsReviewsData]);

    useEffect(() => {
        if (study) {
            loadData({ variables: { studyId: study.id } });
            loadStudyData({ variables: { id: study.id } });
            loadDataSets({ variables: { studyId: study.id } });
            loadDataSetsReviews({ variables: { studyId: study.id } });
        }
    }, [study]);

    useEffect(() => {
        const config = state.scoringData?.config ?? state.studyData?.config;
        if (config) {
            setPanels(getDashboardPanels(config.dashboards, config.panels));
        }
    }, [state.scoringData, state.studyData]);

    useEffect(() => {
        if (studyData?.study)
            setOwnerOptions(
                studyData.study.members.map((member: StudyFields_Study_members_StudyMember) => ({
                    id: member.id,
                    text: `${member.firstName} ${member.lastName}`,
                    active: member.active,
                })),
            );
    }, [studyData]);

    useEffect(() => {
        if (data?.centralMonitoringObservations?.length && ownerOptions) {
            const newArray: CentralMonitoringActionFields[] = [];
            data.centralMonitoringObservations.forEach((copiedItem: ICMObservationAndActionExtended) => {
                const item = { ...copiedItem };
                const _activeActions = item.actions?.filter((action: CentralMonitoringActionFields) => !action.deleted);
                const recentActionDate = _activeActions
                    .map((action: CentralMonitoringActionFields) => action.requestDate)
                    .sort()
                    .reverse()[0];
                const numberOfOpenActions = _activeActions.filter(
                    (action: CentralMonitoringActionFields) => !action.completedDate,
                );

                const foundSite = dataSites?.sites?.find((el: SiteFields) => el.id === item.siteId);
                let _site = foundSite ? { ...foundSite } : '';
                if (_site === '' && siteDeletedData?.sitesDelete?.length > 0) {
                    const deletedSite = siteDeletedData?.sitesDelete?.find((site: any) => site.id === item.siteId);
                    _site = deletedSite ? { ...deletedSite } : null;
                    if (_site) {
                        _site.Fields = {
                            'Site.name': _site.name,
                            'Site.id': _site.id,
                            'Site.status': _site.status,
                        };
                        _site.Deleted = true;
                    }
                }
                item._recentActionDate = recentActionDate;
                item._openAction = numberOfOpenActions.length;
                item._numberOfActions = _activeActions?.length;
                item._owner = item.ownerId
                    ? ownerOptions.find((el: SelectWithSearchDataArrayInterface) => el.id === item.ownerId)
                    : null;
                item._datasetDate = item.dataSet?.dataCutDate ?? null;
                item._panelName = getPanelName(item.panel, item.type);
                item._site = _site;
                item._siteName = item._site?.name ?? '';
                item._siteDeleted = _site?.Deleted ?? false;
                item._isClosedDateReadOnly =
                    item.actions.findIndex((el: CentralMonitoringActionFields) => !el.completedDate && !el.deleted) >=
                    0;
                if (item.deleted && item.audit) {
                    item._deleteReason = item.audit.reason;
                    item._deleteDate = item.audit.created;
                    item._deletedBy = `${item.audit.userFirstName} ${item.audit.userLastName}`;
                }
                if (item.actions && item.actions.length) {
                    item.actions = item.actions.map((element) => {
                        const owner: SelectWithSearchDataArrayInterface = element.ownerId
                            ? ownerOptions.find((el: SelectWithSearchDataArrayInterface) => el.id === element.ownerId)
                            : null;
                        return {
                            ...element,
                            observation: item,
                            sites: dataSites?.sites,
                            observationId: item.id,
                            _obsDeleted: item.deleted,
                            _ownerLabel: owner ? owner.text : '',
                            _owner: owner ?? '',
                            _site,
                            _siteName: _site?.name ?? '',
                            _siteDeleted: _site?.Deleted ?? false,
                            _isCompletedDateReadOnly: Boolean(item.closedDate),
                            ...(element.deleted && element.audit
                                ? {
                                      _deleteReason: element.audit.reason,
                                      _deleteDate: element.audit.created,
                                      _deletedBy: `${element.audit.userFirstName} ${element.audit.userLastName}`,
                                  }
                                : {}),
                        };
                    });
                    newArray.push(...item.actions);
                }
            });
            setActions(newArray);
        }
    }, [data, dataSites, ownerOptions]);

    const rowActions = (action: any) => {
        if (action?.deleted) {
            return [
                {
                    label: 'cm:restoreAction',
                    icon: ['fal', 'undo'],
                    onClick: () => handleRestoreAct(action),
                    enabled: canEdit,
                },
            ];
        }

        return [
            {
                label: 'ra:editAction',
                icon: ['fal', 'edit'],
                onClick: () => {
                    setCurrentAction(action);
                    addActionOpen.setTrue();
                },
                enabled: canEdit,
            },
            {
                label: 'cm:deleteAction',
                icon: ['fal', 'trash'],
                onClick: () => handleDeleteAct(action),
                enabled: canEdit,
            },
            {
                label: 'cm:viewSiteDashboard',
                icon: ['far', 'eye'],
                onClick: () => {
                    NavigationService.setViewLegacySiteDashboard();
                    navigate(AllRoutes.path('cm-site-dashboard') as string, {
                        state: {
                            skipSelectDate: true,
                            showDashboard: true,
                            siteId: action._site.id as string | null | undefined,
                        },
                    });
                },
                enabled:
                    action._siteDeleted === false && action._site
                        ? (studyData.study.sitesStatusesToExclude?.indexOf(action._site.status) < 0 ?? true)
                        : false,
            },
        ];
    };

    if (!study) return <h1>{t('noStudySelected')}</h1>;

    if (error) return <Error error={error} />;

    if (loading || !data || loadingStudy) return <Loading float />;

    if (!canView) return <h1>{t('unauthorised')}</h1>;

    return (
        <>
            <ManageObservation
                study={study}
                canEdit={canEdit}
                owners={ownerOptions}
                currentObservation={currentObservation}
                type={currentObservation?.type}
                open={manageObservationOpen.value}
                panels={panels}
                panelId={currentObservation?.panelId}
                sites={dataSites?.sites ?? []}
                onClose={(action: string | null) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'edit')
                            NotificationService.showNotification(
                                `${t('ra:observation')} ${currentObservation.recordId} ${t('ra:successfulUpdated')}`,
                            );
                        setCurrentObservation(null);
                        manageObservationOpen.setFalse();
                    }
                }}
                datasetDates={datasetDates}
            />
            <AddAction
                study={study}
                owners={ownerOptions}
                currentAction={currentAction}
                open={addActionOpen.value}
                canEdit={canEdit}
                onClose={(action: string | null) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'edit')
                            NotificationService.showNotification(
                                `${t('subject:action')} ${currentAction.recordId} ${t('ra:successfulUpdated')}`,
                            );
                        addActionOpen.setFalse();
                        setCurrentAction(null);
                    }
                }}
            />
            <PageTitle text={t('ra:manageActions')} />
            <ActionsGridV2
                title={t('ra:observationsAndActions')}
                study={studyData?.study}
                actions={actions}
                observations={data.centralMonitoringObservations}
                editObservation={(observation: riskObservations_OpraQueries_riskObservations_RiskObservation) => {
                    setCurrentObservation(observation);
                    manageObservationOpen.setTrue();
                }}
                canEdit={canEdit}
                rowActions={rowActions}
            />
        </>
    );
};

export default ManageCMActions;
